import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LOGO_URL } from "@/lib/useReveal";
import { useUserAuth } from "@/user/UserAuthContext";
import { ROLE_LABEL } from "@/user/dashboards.config";
import { pathForRole, userApi } from "@/user/api";

const SIDEBAR = [
    { to: "", label: "Dashboard", icon: iconGrid, end: true },
    { to: "workspace", label: "My Workspace", icon: iconBriefcase },
    { to: "submissions", label: "Submissions", icon: iconInbox },
    { to: "tasks", label: "Tasks", icon: iconCheck },
    { to: "documents", label: "Documents", icon: iconFile },
    { to: "settings", label: "Settings", icon: iconGear },
];

export default function AppShell() {
    const { user, company, status, logout } = useUserAuth();
    const location = useLocation();
    const nav = useNavigate();
    const [bellOpen, setBellOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);

    useEffect(() => {
        if (status !== "ready") return;
        userApi
            .get("/user/notifications")
            .then((r) => setNotifs(r.data || []))
            .catch(() => {});
    }, [status]);

    if (status === "loading") {
        return (
            <div className="dot-grid min-h-screen flex items-center justify-center text-neutral-500">
                Loading workspace…
            </div>
        );
    }
    if (status === "guest" || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Force first-login password change before any workspace access
    if (user.must_change_password) {
        return <Navigate to="/change-password" replace />;
    }

    // Access guards
    if (user.status === "suspended") {
        return <BlockScreen title="Your account is suspended." body="Please contact your company admin." />;
    }
    const rolePath = pathForRole(user.role);
    if (!rolePath && location.pathname === "/app") {
        return <BlockScreen title="Your role is not assigned." body="Please contact your company admin." />;
    }

    // If we're on /app root, send to role dashboard
    if (location.pathname === "/app") {
        return <Navigate to={rolePath || "/app/profile"} replace />;
    }

    // Role-workspace access enforcement: only CEO / admins can access other-role paths
    const currentWorkspace = detectWorkspaceFromPath(location.pathname);
    const isPrivileged = ["ceo", "company_admin", "platform_admin"].includes(user.role);
    const isOwnWorkspace = currentWorkspace && currentWorkspace === user.role;
    const isSharedRoute = ["profile", "settings", "workspace", "submissions", "tasks", "documents"].some((s) => location.pathname === `/app/${s}`);
    if (currentWorkspace && !isPrivileged && !isOwnWorkspace && !isSharedRoute) {
        return (
            <ShellFrame
                user={user}
                company={company}
                sidebar={<Sidebar user={user} />}
                notifs={notifs}
                bellOpen={bellOpen}
                setBellOpen={setBellOpen}
                profileOpen={profileOpen}
                setProfileOpen={setProfileOpen}
                onLogout={async () => {
                    await logout();
                    nav("/login", { replace: true });
                }}
            >
                <BlockScreen
                    title="You do not have permission to access this workspace."
                    body={`Your role is ${ROLE_LABEL[user.role] || user.role}. Ask your company admin if this seems wrong.`}
                />
            </ShellFrame>
        );
    }

    return (
        <ShellFrame
            user={user}
            company={company}
            sidebar={<Sidebar user={user} />}
            notifs={notifs}
            bellOpen={bellOpen}
            setBellOpen={setBellOpen}
            profileOpen={profileOpen}
            setProfileOpen={setProfileOpen}
            onLogout={async () => {
                await logout();
                nav("/login", { replace: true });
            }}
        >
            <Outlet context={{ user, company }} />
        </ShellFrame>
    );
}

function detectWorkspaceFromPath(pathname) {
    const map = {
        "/app/ceo": "ceo",
        "/app/sales": "sales",
        "/app/finance": "finance",
        "/app/hr": "hr",
        "/app/operations": "operations",
        "/app/marketing": "marketing",
        "/app/customer-support": "customer",
        "/app/procurement": "procurement",
        "/app/legal": "legal",
        "/app/strategy": "strategy",
        "/app/meeting-admin": "meeting",
    };
    return map[pathname] || null;
}

function ShellFrame({ children, user, company, sidebar, notifs, bellOpen, setBellOpen, profileOpen, setProfileOpen, onLogout }) {
    return (
        <div className="dot-grid min-h-screen w-full flex text-black">
            <aside
                data-testid="app-sidebar"
                className="hidden lg:flex flex-col w-[240px] shrink-0 sticky top-0 h-screen px-4 py-6 border-r border-black/[0.06] bg-white/70 backdrop-blur-xl"
            >
                {sidebar}
            </aside>
            <div className="flex-1 min-w-0 flex flex-col">
                <TopBar
                    user={user}
                    company={company}
                    notifs={notifs}
                    bellOpen={bellOpen}
                    setBellOpen={setBellOpen}
                    profileOpen={profileOpen}
                    setProfileOpen={setProfileOpen}
                    onLogout={onLogout}
                />
                <main className="flex-1 px-6 lg:px-10 py-8 lg:py-10">{children}</main>
            </div>
        </div>
    );
}

function Sidebar({ user }) {
    return (
        <>
            <div className="flex items-center gap-2.5 px-1.5">
                <img src={LOGO_URL} alt="" className="logo-blend h-9 w-9 object-contain" />
                <div>
                    <div className="font-display text-[15.5px] font-medium leading-none">
                        CEO<span className="text-neutral-500">’s</span>{" "}
                        <span className="italic">GPT</span>
                    </div>
                    <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-violet-600 mt-1">
                        Workspace
                    </div>
                </div>
            </div>

            <nav className="mt-8 flex-1 space-y-0.5">
                {user.role === "ceo" && (
                    <>
                        <NavLink to="/app/chat" data-testid="app-sidebar-command-chat"
                            className={({ isActive }) => `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] transition-all ${isActive ? "bg-black text-white shadow-[0_10px_30px_-15px_rgba(139,92,246,0.55)]" : "text-neutral-700 hover:bg-black/[0.04]"}`}>
                            <span className="inline-flex items-center justify-center h-6 w-6">{iconChat()}</span>
                            Command Chat
                        </NavLink>
                        <NavLink to="business-scan" data-testid="app-sidebar-business-scan"
                            className={({ isActive }) => `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] transition-all ${isActive ? "bg-black text-white shadow-[0_10px_30px_-15px_rgba(139,92,246,0.55)]" : "text-neutral-700 hover:bg-black/[0.04]"}`}>
                            <span className="inline-flex items-center justify-center h-6 w-6">{iconScan()}</span>
                            Business Scan
                        </NavLink>
                    </>
                )}
                {SIDEBAR.map((n) => (
                    <NavLink
                        key={n.to || "dashboard"}
                        to={n.to || pathForRole(user.role) || "profile"}
                        end={n.end}
                        data-testid={`app-sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                            `group flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] transition-all ${
                                isActive
                                    ? "bg-black text-white shadow-[0_10px_30px_-15px_rgba(139,92,246,0.55)]"
                                    : "text-neutral-700 hover:bg-black/[0.04]"
                            }`
                        }
                    >
                        <span className="inline-flex items-center justify-center h-6 w-6">
                            {n.icon()}
                        </span>
                        {n.label}
                    </NavLink>
                ))}
            </nav>

            <div className="mt-4 rounded-2xl bg-white border border-black/5 p-3 text-[11.5px] text-neutral-500">
                Logged in as
                <div className="mt-1 flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-black text-white text-[10px] font-semibold inline-flex items-center justify-center">
                        {(user.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <span className="font-semibold text-black truncate">{user.name}</span>
                </div>
                <div className="text-[10.5px] text-neutral-500 mt-1 truncate">
                    {ROLE_LABEL[user.role] || user.role}
                </div>
            </div>
        </>
    );
}

function TopBar({ user, company, notifs, bellOpen, setBellOpen, profileOpen, setProfileOpen, onLogout }) {
    return (
        <header
            data-testid="app-topbar"
            className="sticky top-0 z-30 flex items-center justify-between gap-4 px-5 lg:px-10 h-16 border-b border-black/[0.06] bg-white/75 backdrop-blur-xl"
        >
            <div className="flex items-center gap-3 min-w-0">
                <span className="lg:hidden inline-flex items-center gap-2">
                    <img src={LOGO_URL} alt="" className="logo-blend h-7 w-7 object-contain" />
                </span>
                <div className="min-w-0" data-testid="topbar-company">
                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500 leading-none">
                        {company?.name ? "Company" : "Workspace"}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="font-display text-[14px] text-black truncate max-w-[220px]">
                            {company?.name || "No company assigned"}
                        </span>
                        {company?.status && (
                            <span className="text-[10px] font-semibold tracking-[0.14em] uppercase text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">
                                {company.status}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <span className="hidden md:inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1" data-testid="topbar-role">
                    {ROLE_LABEL[user.role] || user.role}
                </span>
                {user.department && user.department.toLowerCase() !== String(ROLE_LABEL[user.role] || user.role).toLowerCase() && (
                    <span className="hidden lg:inline-flex items-center text-[11px] font-medium tracking-[0.1em] uppercase text-neutral-600 bg-black/[0.04] rounded-full px-2.5 py-1" data-testid="topbar-department">
                        {user.department}
                    </span>
                )}

                {/* Bell */}
                <div className="relative">
                    <button
                        onClick={() => { setBellOpen((v) => !v); setProfileOpen(false); }}
                        data-testid="topbar-bell"
                        className="relative h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-black/[0.04]"
                        aria-label="Notifications"
                    >
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                            <path d="M18 16v-5a6 6 0 10-12 0v5l-2 2h16l-2-2zM10 20a2 2 0 004 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        {notifs.some((n) => !n.read) && (
                            <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-violet-500 ring-2 ring-white" />
                        )}
                    </button>
                    {bellOpen && (
                        <div data-testid="topbar-bell-panel" className="absolute right-0 mt-2 w-80 glass-strong rounded-2xl p-2 z-40">
                            <div className="px-3 pt-2 text-[10px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                                Notifications
                            </div>
                            <ul className="mt-2 max-h-72 overflow-auto">
                                {(notifs || []).map((n) => (
                                    <li key={n.id} className="p-3 hover:bg-black/[0.03] rounded-xl">
                                        <div className="text-[13px] font-semibold text-black">{n.title}</div>
                                        <div className="text-[12px] text-neutral-500 mt-0.5">{n.body}</div>
                                    </li>
                                ))}
                                {(!notifs || notifs.length === 0) && (
                                    <li className="p-4 text-[13px] text-neutral-500">You’re all caught up.</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Profile */}
                <div className="relative">
                    <button
                        onClick={() => { setProfileOpen((v) => !v); setBellOpen(false); }}
                        data-testid="topbar-profile"
                        className="flex items-center gap-2 rounded-full bg-white border border-black/10 px-2 py-1.5 pr-3 hover:bg-neutral-50"
                    >
                        <span className="h-6 w-6 rounded-full bg-black text-white text-[11px] font-semibold inline-flex items-center justify-center">
                            {(user.name || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="text-[12px] font-semibold text-black hidden sm:inline max-w-[110px] truncate">
                            {user.name}
                        </span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    {profileOpen && (
                        <div data-testid="topbar-profile-menu" className="absolute right-0 mt-2 w-64 glass-strong rounded-2xl p-2 z-40">
                            <div className="px-3 py-2">
                                <div className="text-[13px] font-semibold text-black" data-testid="topbar-profile-name">{user.name}</div>
                                <div className="text-[11.5px] text-neutral-500">{user.email}</div>
                            </div>
                            <div className="my-1 h-px bg-black/[0.06]" />
                            <NavLink to="profile" onClick={() => setProfileOpen(false)} data-testid="topbar-profile-link" className="block px-3 py-2 text-[13px] rounded-xl hover:bg-black/[0.04]">
                                Profile
                            </NavLink>
                            <NavLink to="settings" onClick={() => setProfileOpen(false)} data-testid="topbar-settings-link" className="block px-3 py-2 text-[13px] rounded-xl hover:bg-black/[0.04]">
                                Settings
                            </NavLink>
                            <div className="my-1 h-px bg-black/[0.06]" />
                            <button
                                onClick={onLogout}
                                data-testid="topbar-logout"
                                className="w-full text-left px-3 py-2 text-[13px] rounded-xl text-rose-600 hover:bg-rose-50"
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

function BlockScreen({ title, body }) {
    return (
        <div data-testid="access-block" className="min-h-[60vh] flex items-center justify-center px-6">
            <div className="glass-strong rounded-3xl p-8 sm:p-12 max-w-lg text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-violet-50 text-violet-600 inline-flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl font-medium text-black">{title}</h2>
                {body && <p className="mt-3 text-[14px] text-neutral-600">{body}</p>}
            </div>
        </div>
    );
}

/* Icons */
function iconChat() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21 12a8 8 0 01-11.5 7.2L4 20l1.2-4.5A8 8 0 1121 12z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
function iconScan() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M4 7V5a1 1 0 011-1h2M17 4h2a1 1 0 011 1v2M20 17v2a1 1 0 01-1 1h-2M7 20H5a1 1 0 01-1-1v-2M4 12h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function iconGrid() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );
}
function iconBriefcase() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function iconInbox() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M3 13l3-8h12l3 8v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6zM3 13h5l2 3h4l2-3h5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
function iconChart() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    );
}
function iconCheck() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
function iconStamp() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M6 20h12M8 16h8l1-6a4 4 0 00-4-4h-2a4 4 0 00-4 4l1 6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
function iconFile() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
            <path d="M14 3v6h6M8 13h8M8 17h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    );
}
function iconGear() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M19 12a7 7 0 00-.1-1.2l2.1-1.6-2-3.4-2.5.8a7 7 0 00-2-1.2L14 3h-4l-.5 2.4c-.7.3-1.4.7-2 1.2L5 5.8l-2 3.4L5.1 10.8A7 7 0 005 12c0 .4 0 .8.1 1.2L3 14.8l2 3.4 2.5-.8c.6.5 1.3.9 2 1.2L10 21h4l.5-2.4c.7-.3 1.4-.7 2-1.2l2.5.8 2-3.4-2.1-1.6c.1-.4.1-.8.1-1.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
    );
}
